/**
 * WebRTC API Client Base Class
 *
 * Base class that abstracts WebRTC data channel communication into a simple HTTP-like API.
 * Handles sending API requests over the data channel and resolving responses
 * using a Promise-based interface.
 *
 * Extend this class to add typed API methods specific to your API.
 *
 * @example
 * class MyCameraClient extends WebrtcApiClientBase {
 *   async getStatus() {
 *     const response = await this.request({
 *       method: "GET",
 *       path: "/status",
 *       headers: {},
 *       body: null
 *     })
 *     return JSON.parse(response.body)
 *   }
 * }
 */

import type { components } from "./remote-management-api"

/**
 * WebRTC request message type from OpenAPI spec.
 * Note: The 'id' field is auto-generated by the client and not required in the input.
 */
export type ApiRequest = Omit<components["schemas"]["WebRTCRequest"], "id">

/**
 * WebRTC response message type from OpenAPI spec.
 */
export type ApiResponse = components["schemas"]["WebRTCResponse"]

interface PendingRequest {
  resolve: (response: ApiResponse) => void
  reject: (error: Error) => void
  timeout: number
}

interface PendingStreamingRequest {
  // For streaming requests, we store a callback that gets called for each response
  onResponse: (response: ApiResponse) => void
  onError: (error: Error) => void
  timeout: number
}

export class WebrtcApiClientBase {
  protected dataChannel: RTCDataChannel
  protected pendingRequests: Map<string, PendingRequest> = new Map()
  protected pendingStreamingRequests: Map<string, PendingStreamingRequest> = new Map()
  protected requestTimeoutMs: number = 30000 // 30 seconds default

  /**
   * Create a new WebRTC API Client Base
   * @param dataChannel The RTCDataChannel to use for communication
   * @param requestTimeoutMs Optional timeout in milliseconds for requests (default: 30000)
   */
  constructor(dataChannel: RTCDataChannel, requestTimeoutMs?: number) {
    this.dataChannel = dataChannel
    if (requestTimeoutMs) {
      this.requestTimeoutMs = requestTimeoutMs
    }

    // Set up message handler
    this.dataChannel.onmessage = (event) => {
      this.handleMessage(event)
    }
  }

  /**
   * Send an API request over the data channel and await the response
   * Protected method - extend this class to create typed API methods
   * @param request The API request to send
   * @returns Promise that resolves with the API response
   */
  protected async request(request: ApiRequest): Promise<ApiResponse> {
    if (this.dataChannel.readyState !== "open") {
      throw new Error("Data channel is not open")
    }

    // Generate unique request ID
    const requestId = `req-${Date.now()}-${Math.random().toString(36).substring(7)}`

    // Create promise for this request
    const promise = new Promise<ApiResponse>((resolve, reject) => {
      // Set up timeout
      const timeout = window.setTimeout(() => {
        this.pendingRequests.delete(requestId)
        reject(new Error(`Request timeout after ${this.requestTimeoutMs}ms`))
      }, this.requestTimeoutMs)

      // Store pending request
      this.pendingRequests.set(requestId, {
        resolve,
        reject,
        timeout,
      })
    })

    // Send request over data channel
    // Format matches lib/cloud-client/src/webrtc/types.rs ApiRequest
    const message = JSON.stringify({
      id: requestId,
      method: request.method,
      path: request.path,
      headers: request.headers,
      body: request.body,
    })

    console.log("[WebRTC API Client] Sending request:", requestId, request.method, request.path)
    this.dataChannel.send(message)

    return promise
  }

  /**
   * Send a streaming API request that returns an async generator
   * Each response with the same request ID will yield a new value
   * @param request The API request to send
   * @returns AsyncGenerator that yields API responses
   */
  protected async *streamingRequest(request: ApiRequest): AsyncGenerator<ApiResponse> {
    if (this.dataChannel.readyState !== "open") {
      throw new Error("Data channel is not open")
    }

    // Generate unique request ID
    const requestId = `req-${Date.now()}-${Math.random().toString(36).substring(7)}`

    // Create a queue to buffer responses
    const responseQueue: ApiResponse[] = []
    let resolveNext: ((value: IteratorResult<ApiResponse>) => void) | null = null
    let streamError: Error | null = null
    let streamEnded = false

    // Set up timeout
    const timeout = window.setTimeout(() => {
      streamError = new Error(`Streaming request timeout after ${this.requestTimeoutMs}ms`)
      streamEnded = true
      this.pendingStreamingRequests.delete(requestId)
      if (resolveNext) {
        resolveNext({ done: true, value: undefined })
        resolveNext = null
      }
    }, this.requestTimeoutMs)

    // Store pending streaming request
    this.pendingStreamingRequests.set(requestId, {
      onResponse: (response: ApiResponse) => {
        if (resolveNext) {
          // Someone is waiting for the next value
          resolveNext({ done: false, value: response })
          resolveNext = null
        } else {
          // Buffer it for later
          responseQueue.push(response)
        }
      },
      onError: (error: Error) => {
        streamError = error
        streamEnded = true
        this.pendingStreamingRequests.delete(requestId)
        window.clearTimeout(timeout)
        if (resolveNext) {
          resolveNext({ done: true, value: undefined })
          resolveNext = null
        }
      },
      timeout,
    })

    // Send request over data channel
    const message = JSON.stringify({
      id: requestId,
      method: request.method,
      path: request.path,
      headers: request.headers,
      body: request.body,
    })

    console.log("[WebRTC API Client] Sending streaming request:", requestId, request.method, request.path)
    this.dataChannel.send(message)

    // Generator loop
    try {
      while (!streamEnded) {
        // Check if we have buffered responses
        if (responseQueue.length > 0) {
          const response = responseQueue.shift()!
          yield response
          continue
        }

        // Wait for next response
        const result = await new Promise<IteratorResult<ApiResponse>>((resolve) => {
          resolveNext = resolve
        })

        if (result.done) {
          break
        }

        yield result.value
      }

      // If there was an error, throw it
      if (streamError) {
        throw streamError
      }
    } finally {
      // Clean up
      window.clearTimeout(timeout)
      this.pendingStreamingRequests.delete(requestId)
    }
  }

  /**
   * Handle incoming messages from the data channel
   */
  private handleMessage(event: MessageEvent): void {
    let message: string

    // Handle both text and binary messages
    if (event.data instanceof ArrayBuffer) {
      const decoder = new TextDecoder("utf-8")
      message = decoder.decode(event.data)
    } else if (typeof event.data === "string") {
      message = event.data
    } else {
      console.error("[WebRTC API Client] Unknown message type:", typeof event.data)
      return
    }

    try {
      const response = JSON.parse(message) as ApiResponse

      console.log("[WebRTC API Client] Received response:", response.id, response.status)

      // Check if this is for a streaming request first
      const streamingPending = this.pendingStreamingRequests.get(response.id)
      if (streamingPending) {
        // Reset the timeout for streaming requests when we receive a response
        window.clearTimeout(streamingPending.timeout)
        const newTimeout = window.setTimeout(() => {
          const pending = this.pendingStreamingRequests.get(response.id)
          if (pending) {
            pending.onError(new Error(`Streaming request timeout after ${this.requestTimeoutMs}ms`))
          }
        }, this.requestTimeoutMs)
        streamingPending.timeout = newTimeout

        // Call the onResponse callback for streaming requests
        streamingPending.onResponse(response)
        return
      }

      // Check if this is for a regular request
      const pending = this.pendingRequests.get(response.id)
      if (!pending) {
        console.warn("[WebRTC API Client] No pending request for response:", response.id)
        return
      }

      // Clear timeout
      window.clearTimeout(pending.timeout)

      // Remove from pending
      this.pendingRequests.delete(response.id)

      // Resolve promise
      pending.resolve(response)
    } catch (error) {
      console.error("[WebRTC API Client] Error parsing response:", error)
    }
  }

  /**
   * Cancel all pending requests
   */
  cleanup(): void {
    // Reject all pending requests
    for (const [requestId, pending] of this.pendingRequests.entries()) {
      window.clearTimeout(pending.timeout)
      pending.reject(new Error("Client cleanup - request cancelled"))
    }
    this.pendingRequests.clear()

    // Cancel all pending streaming requests
    for (const [requestId, pending] of this.pendingStreamingRequests.entries()) {
      window.clearTimeout(pending.timeout)
      pending.onError(new Error("Client cleanup - streaming request cancelled"))
    }
    this.pendingStreamingRequests.clear()
  }

  /**
   * Check if the data channel is open and ready
   */
  get isReady(): boolean {
    return this.dataChannel.readyState === "open"
  }
}
